import React from 'react';
import { Button, Checkbox } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { VariantComboGroupView } from '../../../../services/variants-combos/variants-combos.service';
import { VariantComboStockRows } from './VariantComboStockRows';
import classes from './pos-limit-items-section.module.scss';

const INFINITY_SYMBOL = '∞';

export type LimitItemRowProps = {
  id: number;
  name: string;
  limit: number | null;
  isSelected: boolean;
  isManager: boolean;
  interactionsLocked: boolean;
  groups: VariantComboGroupView[];
  isExpanded: boolean;
  onOpenNumPad: (productId: number) => void;
  onToggleSelected: (productId: number, checked: boolean) => void;
  onClearLimit: (productId: number) => void;
  onToggleExpanded: (productId: number) => void;
  selectedOptionItemIds: Set<number>;
  onToggleOptionItemSelected: (itemId: number, checked: boolean) => void;
  onSetOptionItemInStock: (itemIds: number[], inStock: boolean) => void;
};

// Architectural summary: memoized limit-items row; stable props skip whole-list re-renders.
const LimitItemRowComponent: React.FC<LimitItemRowProps> = ({
  id,
  name,
  limit,
  isSelected,
  isManager,
  interactionsLocked,
  groups,
  isExpanded,
  onOpenNumPad,
  onToggleSelected,
  onClearLimit,
  onToggleExpanded,
  selectedOptionItemIds,
  onToggleOptionItemSelected,
  onSetOptionItemInStock,
}) => {
  const hasLimit = limit !== null;
  const isOutOfStock = limit === 0;
  const isLimited = limit !== null && limit > 0;
  const rowInteractive = isManager && !interactionsLocked;

  return (
    <div
      data-testid={`limit-items-row-${id}`}
      className={`${classes.row} ${!isManager ? classes.rowReadOnly : ''}`}
      role={rowInteractive ? 'button' : undefined}
      tabIndex={rowInteractive ? 0 : undefined}
      onClick={rowInteractive ? () => onOpenNumPad(id) : undefined}
      onKeyDown={
        rowInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenNumPad(id);
              }
            }
          : undefined
      }
    >
      <div className={classes.productCell}>
        {isManager && (
          <span
            className={classes.productCheckbox}
            data-testid={`limit-items-checkbox-container-${id}`}
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              data-testid={`limit-items-select-${id}`}
              checked={isSelected}
              onChange={(event) => onToggleSelected(id, event.currentTarget.checked)}
              aria-label={`בחר ${name}`}
              disabled={interactionsLocked}
            />
            {groups.length > 0 && (
              <button
                type="button"
                className={classes.expandButton}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleExpanded(id);
                }}
                aria-label={isExpanded ? `סגור אפשרויות עבור ${name}` : `הצג אפשרויות עבור ${name}`}
                aria-expanded={isExpanded}
              >
                <FontAwesomeIcon icon={(isExpanded ? faChevronDown : faChevronLeft) as IconProp} />
              </button>
            )}
          </span>
        )}
        {!isManager && groups.length > 0 && (
          <button
            type="button"
            className={classes.expandButton}
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpanded(id);
            }}
            aria-label={isExpanded ? `סגור אפשרויות עבור ${name}` : `הצג אפשרויות עבור ${name}`}
            aria-expanded={isExpanded}
          >
            <FontAwesomeIcon icon={(isExpanded ? faChevronDown : faChevronLeft) as IconProp} />
          </button>
        )}
        <span className={classes.productName}>{name}</span>
      </div>

      <div className={classes.qtyCell}>
        <span
          className={`${classes.qtyBadge} ${isOutOfStock ? classes.qtyBadgeDanger : ''} ${isLimited ? classes.qtyBadgeInfo : ''}`}
          aria-label={isOutOfStock ? 'אזל מהמלאי' : isLimited ? `הגבלה ${limit}` : 'ללא הגבלה'}
        >
          {hasLimit ? limit : INFINITY_SYMBOL}
        </span>
        {isManager && (
          <Button
            type="button"
            data-testid={`limit-items-clear-${id}`}
            className={classes.clearButton}
            onClick={(event) => {
              event.stopPropagation();
              onClearLimit(id);
            }}
            disabled={interactionsLocked || !hasLimit}
            title={!hasLimit ? 'אין הגבלה לאיפוס' : undefined}
          >
            אפס הגבלה
          </Button>
        )}
      </div>

      {isExpanded && (
        <VariantComboStockRows
          groups={groups}
          isManager={isManager}
          interactionsLocked={interactionsLocked}
          selectedItemIds={selectedOptionItemIds}
          onToggleSelected={onToggleOptionItemSelected}
          onSetInStock={onSetOptionItemInStock}
        />
      )}
    </div>
  );
};

export const LimitItemRow = React.memo(LimitItemRowComponent);
